import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { LabOSAuth } from './lib/labos.tsx';
import ErrorBoundary from '@/components/ErrorBoundary';

// LabOS app identifier, loaded from environment variables.
// For deployment: Set the APP_ID repository variable in GitHub (per environment,
// since dev/prod are registered as separate apps in LabOS).
// For local development: Create frontend/.env.local with VITE_APP_ID.
const APP_ID = import.meta.env.VITE_APP_ID;

if (!APP_ID) {
  throw new Error(
    'LabOS app ID missing. Ensure the VITE_APP_ID environment variable is set:\n' +
      '  For local dev: Create frontend/.env.local\n' +
      '  For deployment: Set APP_ID in the GitHub environment (dev/prod)',
  );
}

// Recover from stale bundles after a deploy. Lazy-loaded chunks (e.g. the legal
// review PDF via `import('@/lib/reviewDoc')`) get new content-hashed filenames on
// every deploy, so a tab left open across a deploy requests a chunk that no
// longer exists — the SPA rewrite serves index.html for it, and the dynamic
// import fails ("Failed to fetch dynamically imported module"). Vite fires
// `vite:preloadError` in that case; reload once to pull the fresh manifest.
// A short time-based guard prevents a reload loop if the chunk is genuinely
// unavailable (the error then surfaces to the caller's own handling), and
// re-arms after the window so a later deploy can recover again.
window.addEventListener('vite:preloadError', () => {
  const key = 'aics:lastChunkReload';
  const now = Date.now();
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(key) || 0);
  } catch {
    /* sessionStorage unavailable — fall through and reload */
  }
  if (now - last > 10000) {
    try {
      sessionStorage.setItem(key, String(now));
    } catch {
      /* best-effort */
    }
    window.location.reload();
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <LabOSAuth appId={APP_ID}>
        <App />
      </LabOSAuth>
    </ErrorBoundary>
  </StrictMode>,
);
